import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  ModalContext,
  Select,
  Box,
  Text,
  Form,
  useFormState,
  FormErrorTypes,
  useFormValidation,
  toast,
} from "dash-ui";
import { isNil } from "lodash";
import { FormEvent, useRef } from "react";

import {
  BrandingTypeEnum,
  useBulkUpdateMediaBrandingTypeMutation,
} from "gql/generated.graphql";
import { getBrandingTypeOptions } from "containers/properties/PropertyMedia/helpers/brandingTypeOptions";

enum FormFields {
  BrandingType = "brandingType",
}

const BulkUpdateBrandingModal = ({
  onCompleted,
}: {
  onCompleted: () => void;
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const { payload, isOpenCurrent, toggleModal } = useContext(ModalContext);
  const [bulkUpdateMediaBrandingType, { loading }] =
    useBulkUpdateMediaBrandingTypeMutation();

  const [
    formState,
    [setBrandingType],
    [formOnSubmit, formOnError, , , resetForm],
  ] = useFormState([FormFields.BrandingType]);

  const [formErrors, , [setBrandingTypeError], [resetFormErrors]] =
    useFormValidation([FormFields.BrandingType]);

  const formRef = useRef<HTMLFormElement | null>(null);
  const isOpen = isOpenCurrent["BulkUpdateBrandingModal"];

  const { mediaIds = [], filter, excludedIds, selectedCount } = payload || {};

  const brandingOptions = getBrandingTypeOptions(t);

  const onModalClose = () => {
    toggleModal(false, "BulkUpdateBrandingModal");
    resetFormErrors();
    resetForm();
    setBrandingType("");
  };

  const onSelectChange = (value?: string) => {
    if (value) {
      setBrandingType(value);
      setBrandingTypeError(null);
    }
  };

  const onSubmitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { brandingType } = formState;

    if (!brandingType) {
      return setBrandingTypeError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.selectBrandingType"),
      });
    }

    const hasNotFilter = isNil(filter) || Object.keys(filter).length === 0;

    try {
      await bulkUpdateMediaBrandingType({
        variables: {
          mediaFilesIds: mediaIds,
          brandingType: brandingType as BrandingTypeEnum,
          excludedIds,
          ...(!hasNotFilter &&
            (filter.fileTypes.length > 0 || !!filter.searchText) && { filter }),
        },
      });
      toast.success(
        t("pages:propertyPage.bulkBrandingTypeUpdated", {
          count: selectedCount,
        })
      );
      onCompleted();
      onModalClose();
    } catch (error) {
      console.error("Error updating branding type:", error);
      toast.error(t("common:serverMessages.unknown"));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onModalClose}
      title={t("pages:propertyPage.updateBrandingType") || ""}
      submitButton={{
        title: t("common:labels.update"),
        isLoading: loading,
        disabled: loading || !formState.brandingType,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t("common:labels.cancel"),
        onClick: onModalClose,
      }}
    >
      <Form ref={formRef} onSubmit={onSubmitForm}>
        <Box my={3}>
          <Text>
            {t("pages:propertyPage.bulkUpdateBrandingText", {
              quantity: selectedCount,
            })}
          </Text>
        </Box>
        <Form.Group>
          <Form.Label>{t("common:labels.brandingType")}</Form.Label>
          <Select
            w="100%"
            value={formState.brandingType}
            onChanged={onSelectChange}
            options={brandingOptions}
            placeholder={t("common:placeholders.selectBrandingType") || ""}
            isSearchable={false}
            $errorType={formErrors[FormFields.BrandingType]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.BrandingType]?.type}>
            {formErrors[FormFields.BrandingType]?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default BulkUpdateBrandingModal;
